import { useState, useEffect } from 'react'
import { supabase } from '../api/supabase'
import CountrySelect from './CountrySelect'
import ExistingCompaniesNotice from './ExistingCompaniesNotice'
import { findExistingCompanies, type ExistingCompany } from '../lib/existingCompanies'

interface QuickAddCustomerDialogProps {
  isOpen: boolean
  initialName: string
  // Which address_type role to assign the new customer immediately (e.g.
  // 'Cus') -- without this, useCustomersByRole('Cus') (what CustomerCombobox
  // itself is backed by) wouldn't see the new customer at all until someone
  // separately visits its detail page and ticks the role on, defeating the
  // point of "quick add so staff aren't stuck".
  role: string
  onClose: () => void
  onSuccess: (customer: { id: string; code: string; name: string }) => void
}


// Same generation approach as CreateCustomerDialog.tsx (up to 3 initials +
// a random 4-digit suffix) -- kept independent rather than shared since this
// dialog is intentionally a much smaller subset of that one's fields.
function generateCode(name: string): string {
  const initials = name.trim().split(/\s+/).slice(0, 3).map(w => w[0]?.toUpperCase() ?? '').join('')
  const randomNum = Math.floor(1000 + Math.random() * 9000)
  return `${initials}${randomNum}`
}

export default function QuickAddCustomerDialog({ isOpen, initialName, role, onClose, onSuccess }: QuickAddCustomerDialogProps) {
  const [code, setCode] = useState('')
  const [name, setName] = useState(initialName)
  const [country, setCountry] = useState('GB')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [existing, setExisting] = useState<ExistingCompany[] | null>(null)

  useEffect(() => {
    if (isOpen) {
      setExisting(null)
      setName(initialName)
      setCode(generateCode(initialName))
      setCountry('GB')
      setError(null)
    }
  }, [isOpen, initialName])

  if (!isOpen) return null

  // Gives the company the role this picker is backed by (so it shows up in the picker). Non-fatal: the company itself is already saved
  // and staff can tick the role on its page.
  async function tagRole(customerId: string) {
      const { data: addressType } = await supabase.from('address_types').select('id').eq('code', role).maybeSingle()
      if (addressType) {
        const { error: roleErr } = await supabase.from('customer_address_types').insert([{ customer_id: customerId, address_type_id: addressType.id }])
        // Non-fatal -- the customer record itself saved fine; staff can tick
        // the role on manually from the customer's detail page if this fails.
        if (roleErr) console.error('[QuickAddCustomerDialog] Failed to assign role:', roleErr)
      }

  }

  async function handleSave(skipExistingCheck = false) {
    if (!code.trim()) { setError('Code is required'); return }
    if (!name.trim()) { setError('Name is required'); return }
    setSaving(true)
    setError(null)
    try {
      if (!skipExistingCheck) {
        // Fresh query against the database: prove the company does not exist before creating it.
        const found = await findExistingCompanies(name, code)
        if (found.length > 0) { setExisting(found); setSaving(false); return }
      }
      const { data: customer, error: insertErr } = await supabase
        .from('customers')
        .insert([{ code: code.trim().toUpperCase(), name: name.trim(), country, country_code: country, is_active: true }])
        .select('id, code, name')
        .single()
      if (insertErr) throw new Error(insertErr.message)

      await tagRole(customer.id)

      onSuccess(customer)
      onClose()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create customer')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50" onClick={e => { if (e.target === e.currentTarget) onClose() }}>
      <div className="w-full max-w-md bg-white dark:bg-gray-800 rounded-xl shadow-xl p-6">
        <h2 className="text-lg font-bold text-gray-900 dark:text-white mb-1">Quick Add Customer</h2>
        <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
          Creates a stub record so you can carry on -- add the rest of their details later from the customer's page.
        </p>

        {error && (
          <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-sm text-red-700 dark:text-red-400">{error}</div>
        )}

        {existing && (
          <ExistingCompaniesNotice
            typedName={name.trim()}
            matches={existing}
            busy={saving}
            onUse={async c => { await tagRole(c.id); onSuccess({ id: c.id, code: c.code, name: c.name }); onClose() }}
            onCreateAnyway={() => { setExisting(null); handleSave(true) }}
          />
        )}

        <div className="space-y-3">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Code <span className="text-red-500">*</span></label>
            <input type="text" value={code} onChange={e => setCode(e.target.value.toUpperCase())}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Name <span className="text-red-500">*</span></label>
            <input type="text" value={name} onChange={e => setName(e.target.value)} placeholder="Company name"
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Country</label>
            <CountrySelect value={country} onChange={setCountry} />
          </div>
        </div>

        <div className="mt-6 flex justify-end gap-3">
          <button onClick={onClose} disabled={saving}
            className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50">
            Cancel
          </button>
          <button onClick={() => handleSave()} disabled={saving}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:bg-gray-400">
            {saving ? 'Saving...' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  )
}
